import { baseApiV2 } from '@verantos-dev/app-state-base';
import { PROJECTS_BASE_PATH } from '@verantos-dev/core-constants';
import {
  type Cohort,
  type CohortVersion,
  type CohortCRUDResponse,
  type DeleteCohortData,
  type CreateCohortWithDatasetData,
  type RenameCohortData,
  CreateCohortVersionRequestBody,
  UpdateCohortVersionRequestBody,
} from '@verantos-dev/types-cohorts';
import { type ListResponse, type ObjectResponse } from '@verantos-dev/types-common';

import { COHORTS_BASE_PATH } from './constants';

export const cohortApiSlice = baseApiV2
  .enhanceEndpoints({
    addTagTypes: ['cohorts', 'cohortAttrition', 'cohortSummary', 'subjects', 'projects'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      /** Cohort Profile Endpoints */

      getCohorts: builder.query<
        ListResponse<Cohort>,
        { projectId: string; urlParams: string | null }
      >({
        query: ({ projectId, urlParams }) => {
          const params = urlParams ? `${encodeURI(urlParams)}` : '';
          return `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}${params}`;
        },
        providesTags: ['cohorts'],
      }),

      getCohort: builder.query<ObjectResponse<Cohort>, { projectId: string; cohortId: string }>({
        query: ({ projectId, cohortId }) =>
          `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}`,
        providesTags: (result) => [{ type: 'cohorts', id: result?.data?.cohortId }],
      }),

      createCohort: builder.mutation<
        ObjectResponse<Cohort>,
        { projectId: string; data: CreateCohortWithDatasetData }
      >({
        query: ({ projectId, data }) => ({
          url: `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}`,
          method: 'POST',
          body: { ...data },
        }),
        invalidatesTags: ['cohorts', 'projects'],
      }),

      deleteCohort: builder.mutation<CohortCRUDResponse, DeleteCohortData>({
        query: ({ projectId, cohortId }) => ({
          url: `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, { cohortId }) => [
          { type: 'cohorts', id: cohortId },
          'cohorts',
          'projects',
        ],
      }),
      renameCohort: builder.mutation<CohortCRUDResponse, RenameCohortData>({
        query: ({ cohortId, projectId, data }) => ({
          url: `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}/rename`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (result, error, { cohortId }) => [
          { type: 'cohorts', id: cohortId },
          'cohorts',
          'projects',
        ],
      }),

      /** Cohort Version Endpoints */
      getCohortVersion: builder.query<
        ObjectResponse<CohortVersion>,
        { projectId: string; cohortId: string; cohortVersionId: string }
      >({
        query: ({ projectId, cohortId, cohortVersionId }) =>
          `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}/cohort-version/${cohortVersionId}`,
        providesTags: (result) => [{ type: 'cohorts', id: result?.data?.cohortVersionId }],
      }),
      postCohortVersion: builder.mutation<
        ObjectResponse<CohortVersion>,
        { projectId: string; profileId: string; data: CreateCohortVersionRequestBody }
      >({
        query: ({ projectId, profileId, data }) => ({
          url: `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${profileId}/cohort-version`,
          method: 'POST',
          body: { ...data },
        }),
        invalidatesTags: (result) => [
          'cohorts',
          { type: 'cohorts', id: result?.data?.cohortProfileId },
          { type: 'cohortAttrition', id: result?.data?.cohortProfileId },
          { type: 'cohortSummary', id: result?.data?.cohortProfileId },
          { type: 'subjects', id: result?.data?.cohortProfileId },
        ],
      }),
      putCohortVersion: builder.mutation<
        ObjectResponse<CohortVersion>,
        {
          projectId: string;
          profileId: string;
          versionId: string;
          data: UpdateCohortVersionRequestBody;
        }
      >({
        query: ({ projectId, profileId, versionId, data }) => ({
          url: `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${profileId}/cohort-version/${versionId}`,
          method: 'PUT',
          body: { ...data },
        }),
        invalidatesTags: (result) => [
          'cohorts',
          { type: 'cohorts', id: result?.data?.cohortVersionId },
          'projects',
        ],
      }),

      /** Cohort Execution Endpoints */
      postCohortExecution: builder.mutation<
        ObjectResponse<CohortVersion>,
        {
          projectId: string;
          profileId: string;
          versionId?: string;
          datasetReleaseId: string;
          dslVersion?: string;
          dslValue?: string;
          sqlValue?: string;
        }
      >({
        query: ({
          projectId,
          profileId,
          versionId,
          datasetReleaseId,
          dslVersion,
          dslValue,
          sqlValue,
        }) => ({
          url: `/${PROJECTS_BASE_PATH}/${projectId}/${COHORTS_BASE_PATH}/${profileId}/cohort-version/${versionId}/cohort-execution`,
          method: 'POST',
          body: { datasetReleaseId, dslVersion, dslValue, sqlValue },
        }),
        invalidatesTags: (result) => [
          'cohorts',
          { type: 'cohorts', id: result?.data?.cohortProfileId },
          { type: 'cohortAttrition', id: result?.data?.cohortProfileId },
          { type: 'cohortSummary', id: result?.data?.cohortProfileId },
          { type: 'subjects', id: result?.data?.cohortProfileId },
          'projects',
        ],
      }),
    }),
  });
export const {
  /** Cohort Profile Endpoints */
  useGetCohortsQuery,
  useGetCohortQuery,
  useCreateCohortMutation,
  useDeleteCohortMutation,
  useRenameCohortMutation,

  /** Cohort Version Endpoints */
  useGetCohortVersionQuery,
  usePostCohortVersionMutation,
  usePutCohortVersionMutation,

  /** Cohort Execution Endpoints */
  usePostCohortExecutionMutation,
} = cohortApiSlice;
